import { DeviceInfo, DevicePlatform, DeviceType, OSName, BrowserName } from '../models';
import { serverTimestamp } from '@angular/fire/firestore';

/**
 * Detects the device type based on screen width
 */
function detectDeviceType(): DeviceType {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const width = window.innerWidth;
  if (width < 768) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * Detects the operating system from user agent
 */
function detectOS(): { osName: OSName; osVersion: string } {
  if (typeof navigator === 'undefined') {
    return { osName: 'Other', osVersion: 'Unknown' };
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // macOS
  if (userAgent.match(/Macintosh|Mac OS X/i)) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/i);
    const version = match ? match[1].replace('_', '.') : 'Unknown';
    return { osName: 'macOS', osVersion: version };
  }

  // iOS
  if (userAgent.match(/iPhone|iPad|iPod/i)) {
    const match = userAgent.match(/OS (\d+[._]\d+)/i);
    const version = match ? match[1].replace('_', '.') : 'Unknown';
    return { osName: 'iOS', osVersion: version };
  }

  // Windows
  if (userAgent.match(/Windows/i)) {
    const match = userAgent.match(/Windows (?:NT )?(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { osName: 'Windows', osVersion: version };
  }

  // Android
  if (userAgent.match(/Android/i)) {
    const match = userAgent.match(/Android (\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { osName: 'Android', osVersion: version };
  }

  // Linux
  if (userAgent.match(/Linux/i)) {
    return { osName: 'Linux', osVersion: 'Unknown' };
  }

  return { osName: 'Other', osVersion: 'Unknown' };
}

/**
 * Detects the browser from user agent
 */
function detectBrowser(): { browserName: BrowserName; browserVersion: string } {
  if (typeof navigator === 'undefined') {
    return { browserName: 'Other', browserVersion: 'Unknown' };
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Chrome (including Edge Chromium)
  if (userAgent.match(/Chrome/i) && !userAgent.match(/Edg/i) && !userAgent.match(/OPR/i)) {
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { browserName: 'Chrome', browserVersion: version };
  }

  // Edge
  if (userAgent.match(/Edg/i)) {
    const match = userAgent.match(/Edg\/(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { browserName: 'Edge', browserVersion: version };
  }

  // Safari
  if (userAgent.match(/Safari/i) && !userAgent.match(/Chrome/i)) {
    const match = userAgent.match(/Version\/(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { browserName: 'Safari', browserVersion: version };
  }

  // Firefox
  if (userAgent.match(/Firefox/i)) {
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { browserName: 'Firefox', browserVersion: version };
  }

  // Opera
  if (userAgent.match(/OPR/i) || userAgent.match(/Opera/i)) {
    const match = userAgent.match(/(?:OPR|Opera)\/(\d+\.\d+)/i);
    const version = match ? match[1] : 'Unknown';
    return { browserName: 'Opera', browserVersion: version };
  }

  return { browserName: 'Other', browserVersion: 'Unknown' };
}

/**
 * Collects device information from the browser
 * @returns DeviceInfo object with all detected information
 */
export function collectDeviceInfo(): Omit<DeviceInfo, 'updatedAt'> {
  const deviceType = detectDeviceType();
  const { osName, osVersion } = detectOS();
  const { browserName, browserVersion } = detectBrowser();

  return {
    platform: 'web' as DevicePlatform,
    deviceType,
    osName,
    osVersion,
    browserName,
    browserVersion
  };
}

/**
 * Creates a complete DeviceInfo object ready for Firestore
 * @returns DeviceInfo with updatedAt timestamp
 */
export function createDeviceInfo(): DeviceInfo {
  const deviceInfo = collectDeviceInfo();
  return {
    ...deviceInfo,
    updatedAt: serverTimestamp() as any
  };
}
